import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { isStaffEmail } from "@/lib/auth-domain";
import { currentBrand } from "@/lib/brand";

export const Route = createFileRoute("/staff-login")({ component: StaffLoginPage });

function StaffLoginPage() {
  const nav = useNavigate();
  const { profile, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // Redirect to staff today view if already logged in as staff
  useEffect(() => {
    if (!loading && profile) {
      if (profile.role === "staff" || profile.role === "admin") {
        nav({ to: "/app/staff/today" });
      } else {
        toast.error("This account is not a staff profile. Please use the customer login.");
        supabase.auth.signOut();
        nav({ to: "/login" });
      }
    }
  }, [loading, profile, nav]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Block non-staff-domain emails up front so customers can't sign in here.
    if (!isStaffEmail(email)) {
      toast.error("This screen is for Resolute staff. Please use the customer login.");
      return;
    }
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setAuthLoading(false);
    if (error) return toast.error(error.message);

    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.nextLevel === "aal2" && aal.nextLevel !== aal.currentLevel) {
      nav({ to: "/login/mfa" });
      return;
    }

    toast.success("Welcome back!");
    // Redirect will be handled by useEffect when profile loads
  };

  return (
    <div className="grid min-h-screen place-items-center bg-gradient-soft p-6">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-soft">
        <div className="mb-6 flex items-center gap-3">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-hero p-3 shadow-soft">
            <img
              src={currentBrand.logoSrc}
              alt={`${currentBrand.name} logo`}
              className="h-full w-auto"
            />
          </div>
          <div>
            <h1 className="text-xl font-bold">Staff login</h1>
            <p className="text-sm text-muted-foreground">to {currentBrand.name}</p>
          </div>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-2xl"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-2xl"
            />
          </div>
          <Button
            type="submit"
            disabled={authLoading}
            className="w-full rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
          >
            {authLoading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Staff accounts use a @resoluteink.co.uk or @resolute.ink email and are created internally.
        </p>
        <p className="mt-5 text-center text-sm text-muted-foreground">
          Customer?{" "}
          <Link to="/login" className="font-semibold text-primary">
            Use the customer login
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
